import { FormInput } from '@core/components/form';
import { FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import React, { memo, useCallback, useRef } from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, FormGroup, UncontrolledDropdown } from 'reactstrap';

export interface SubjectShortcodeInputProps {
  control: FormControl<string>;
  shortcodes: Record<string, string>;
}

/**
 * A plain subject input with a Short Codes menu that inserts {{code}} at the cursor.
 * The codes are filled in per recipient on the server, like the body's.
 */
export const SubjectShortcodeInput = memo((props: SubjectShortcodeInputProps) => {
  const { control, shortcodes } = props;
  const { fieldState } = useFormField({ control });
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasShortcodes = /\{\{[^{}]*\}\}/.test(R.toString(fieldState.value));

  const insert = useCallback(
    (code: string) => {
      const input = inputRef.current;
      const value = R.toString(control.value);
      const start = input?.selectionStart ?? value.length;
      const end = input?.selectionEnd ?? value.length;
      const token = `{{${code}}}`;

      control.setValue(value.slice(0, start) + token + value.slice(end));

      // Put the cursor back after the inserted code once the input has re-rendered
      requestAnimationFrame(() => {
        input?.focus();
        input?.setSelectionRange(start + token.length, start + token.length);
      });
    },
    [control],
  );

  return (
    <FormGroup>
      <div style={{ position: 'relative' }}>
        <FormInput control={control} withoutGroup innerRef={inputRef} style={{ paddingRight: 130 }} />
        <UncontrolledDropdown style={{ position: 'absolute', top: 4, right: 4 }}>
          <DropdownToggle tag="button" type="button" className="btn btn-sm btn-light" caret>
            Short Codes
          </DropdownToggle>
          <DropdownMenu right style={{ maxHeight: 300, overflowY: 'auto' }}>
            {R.map(R.keys(shortcodes), code =>
              code.startsWith('h:') ? (
                <DropdownItem key={code} header>
                  {shortcodes[code].substring(2)}
                </DropdownItem>
              ) : (
                <DropdownItem key={code} onClick={() => insert(code)}>
                  {shortcodes[code]}
                </DropdownItem>
              ),
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
      {hasShortcodes && (
        <small className="form-text text-muted">
          <i className="bx bx-info-circle mr-1" />
          Short codes are filled in for each recipient, so each person gets their own email.
        </small>
      )}
    </FormGroup>
  );
});
